// Shared Co-Pilot date-picker rule (CR-20260707-001).
//
// The Co-Pilot date pickers (single date + date range) are NOT calendar-date pickers — they pick
// TRADEABLE dates. A tradeable date is a listed option-chain expiration; weekends and any weekday
// with no listed expiration are not tradeable and must not be selectable.
//
// This is the ONE place that rule lives, imported by BOTH Co-Pilots that pick dates: the APG ORION
// roll-positions panel and the OPG ORION date block. (Corrected 2026-09-04: the "Hedge select tab"
// named here before is retired and no hedging-grid file imports this module.) It is deliberately
// shared (owner ruling 2026-07-08): the isolated-sibling rule duplicates STYLE per folder, but this
// is a single correctness rule and safe to share. Pure / Angular-free so it is trivially
// unit-testable and carries no module dependency.
//
// ES5 note: arrays + indexOf only (no Set / for..of), matching the rest of the copilot logic.

// A dx-date-box `disabledDates` callback argument. Typed loosely (not against a DevExtreme import)
// so this stays Angular/DevExtreme-free and AOT-safe. `view` is 'month' | 'year' | 'decade' | ...
export interface DisabledDatesArg {
    date?: Date;
    view?: string;
}

// Local "yyyy-MM-dd" day key — the SAME shape the backend emits for OptionExpirationDescriptor
// .optionExpirationDate (a calendar-date label, timezone-agnostic). Built from local Y/M/D so a
// calendar cell at local midnight maps to its own label.
export function expirationDayKey(d: Date): string {
    const y = d.getFullYear();
    const m = d.getMonth() + 1;
    const day = d.getDate();
    return y + '-' + (m < 10 ? '0' + m : '' + m) + '-' + (day < 10 ? '0' + day : '' + day);
}

// Sat (6) or Sun (0).
export function isWeekendDate(d: Date): boolean {
    const g = d.getDay();
    return g === 0 || g === 6;
}

// The three-letter weekday name of a Date ('Mon'..'Fri'), or null on a weekend. A calendar fact,
// sibling of isWeekendDate — no policy here. The names are the questionnaire's Weekday tokens.
const WEEKDAY_NAMES: (string | null)[] = [null, 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', null];

export function weekdayNameOf(d: Date): string | null {
    return WEEKDAY_NAMES[d.getDay()];
}

// CR-20260904-001 (BR-W5 - supersedes this CR's own earlier marking helper). Build the `disabledDates`
// predicate for the RANGE BOUNDARY date-boxes (Start / End).
//
// A boundary may only be a day the run can actually use, so this is the tradeable rule PLUS the
// WEEKDAY rule (CR-20260915-002, owner ruling 2: every excluded weekday, no longer Mondays only):
//   - not a listed expiration (weekend, holiday, no chain)      -> disabled, permanently. Not liftable.
//   - a day whose weekday is excluded and nobody brought back   -> disabled, LIFTABLE: bring it back
//     in the exclusion calendar and it becomes selectable here.
// Specific-date exclusions do NOT gate the boundary — owner ruling 2026-09-06: the picker is not an
// exclusion editor.
//
// The predicate is DISABLE-only: it never marks. Bringing a day back happens in the exclusion
// calendar and nowhere else, so a struck-but-clickable cell (the old behaviour) no longer exists.
//
// This is ALSO the Start seed's rule: nextSelectableDayAfter walks this same predicate, so the seed
// lands on the first AVAILABLE expiration after the anchor, where "available" already excludes the
// excluded weekdays (ruling 2).
//
// Same view gate as makeExpirationDisabledDates (day cells only), same "returns a NEW function each
// call" contract so rebinding re-applies the widget option. This file decides NO policy: it is TOLD
// which weekdays are excluded and which days are already brought back.
export function makeRangeBoundaryDisabledDates(
    availableExpirations: string[],
    excludedWeekdays: string[],
    weekdayOverrideKeys: string[]
): (arg: DisabledDatesArg) => boolean {
    const notTradeable = makeExpirationDisabledDates(availableExpirations);
    const excluded = excludedWeekdays || [];
    const lifted = weekdayOverrideKeys || [];
    return (arg: DisabledDatesArg): boolean => {
        if (notTradeable(arg)) {
            return true;
        }
        if (excluded.length === 0) {
            return false;
        }
        if (!arg || arg.view !== 'month' || !(arg.date instanceof Date)) {
            return false;   // never gate navigation (year/decade) cells
        }
        const wd = weekdayNameOf(arg.date);
        if (!wd || excluded.indexOf(wd) < 0) {
            return false;
        }
        return lifted.indexOf(expirationDayKey(arg.date)) < 0;
    };
}

// Build the `disabledDates` predicate for a dx-date-box calendar dropdown:
//   - Only DAY cells (view === 'month') are ever gated; month/year/decade cells stay enabled so
//     calendar navigation is never blocked.
//   - Chain loaded (availableExpirations non-empty): a day is DISABLED unless its key is a listed
//     expiration. This removes weekends AND non-expiration weekdays in one rule.
//   - Chain NOT loaded yet (empty list): we cannot validate weekdays, so fall back to disabling
//     WEEKENDS ONLY (owner ruling); the full allow-list applies once the chain arrives and the
//     predicate is rebuilt with a fresh reference.
// Returns a NEW function each call, so binding it to [disabledDates] and rebuilding on the
// availableExpirations change reliably re-applies the widget option.
export function makeExpirationDisabledDates(
    availableExpirations: string[]
): (arg: DisabledDatesArg) => boolean {
    const available = availableExpirations || [];
    const chainLoaded = available.length > 0;
    return (arg: DisabledDatesArg): boolean => {
        if (!arg || arg.view !== 'month' || !(arg.date instanceof Date)) {
            return false;   // never gate navigation (year/decade) cells
        }
        if (!chainLoaded) {
            return isWeekendDate(arg.date);   // pre-load fallback: weekends only
        }
        return available.indexOf(expirationDayKey(arg.date)) < 0;
    };
}

// Pre-select rule (owner request 2026-09-08): when the ORION Date-range tab is presented with NO
// start date, the Start box is seeded with the NEXT selectable day strictly AFTER "today". "Today"
// is the shell's day key (emulated shell clock, or the QG cursor day in a QuoteGenerator shell) —
// never the browser clock; the caller supplies it. "Selectable" is whatever the Start picker itself
// would accept: the SAME disabledDates predicate gates both, so the seed can never land on a day the
// trader could not have clicked (a non-expiration, a weekend, an excluded Monday). Null when no day
// within the look-ahead is selectable (or the key is malformed) — the caller then leaves Start empty.
export function nextSelectableDayAfter(
    todayKey: string,
    disabled: (arg: DisabledDatesArg) => boolean,
    lookaheadDays: number = 90
): Date | null {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(todayKey || '');
    if (!m) {
        return null;
    }
    const y = parseInt(m[1], 10);
    const mo = parseInt(m[2], 10) - 1;
    const d = parseInt(m[3], 10);
    for (let i = 1; i <= lookaheadDays; i++) {
        const candidate = new Date(y, mo, d + i);   // local midnight, the calendar-cell shape
        if (!disabled({ date: candidate, view: 'month' })) {
            return candidate;
        }
    }
    return null;
}
